import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  Grid,
  GridItem,
  Icon,
  IconButton,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AddressCard, Input, Select } from '@shared/components';
import { DocumentTypeEnum } from '@shared/types';
import { GenreEnum } from '@shared/types/Genre';
import { links, removeNonNumeric } from '@shared/utils';
import { Trash } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCreateOnboarding, useGetAllOnboardingInfo } from '../../hooks';
import {
  IOnboardingDataUserForm,
  OnboardingDataUserSchema,
} from '../../schemas';
import { getAddressByPostalCode, ICreateOnboardingInput } from '../../services';

const GENRES = [
  {
    value: GenreEnum.MALE,
    label: 'Masculino',
  },
  {
    value: GenreEnum.FEMALE,
    label: 'Feminino',
  },
];

export const DataUserForm = ({ rootEntity }: { rootEntity: string }) => {
  const { t } = useTranslation(rootEntity);
  const navigate = useNavigate();
  const [postalCodePartial, setPostalCodePartial] = useState(false);

  const { data: onboardingInfo } = useGetAllOnboardingInfo();
  const { mutate: createOnboarding, isLoading } = useCreateOnboarding();

  const hookForm = useForm<IOnboardingDataUserForm>({
    resolver: zodResolver(OnboardingDataUserSchema()),
    shouldFocusError: false,
    defaultValues: {
      document: onboardingInfo?.requestCustomers[0].documentNumber || '',
      name:
        `${onboardingInfo?.requestCustomers[0].firstName} ${onboardingInfo?.requestCustomers[0].lastName}` ||
        '',
      phone: onboardingInfo?.requestCustomers[0].phoneNumber || '',
      email: onboardingInfo?.requestCustomers[0].email || '',
      postalCode: onboardingInfo?.requestPayments[0].postalCode || '',
      number: onboardingInfo?.requestPayments[0].addressNumber || '',
      complement: onboardingInfo?.requestPayments[0].complement || '',
      street: onboardingInfo?.requestPayments[0].street || '',
      district: onboardingInfo?.requestPayments[0].district || '',
      city: onboardingInfo?.requestPayments[0].city || '',
      state: onboardingInfo?.requestPayments[0].state || '',
    },
  });

  const handleSubmit = (data: IOnboardingDataUserForm) => {
    const payload: ICreateOnboardingInput = {
      companyName: data.name,
      displayName: data.name,
      documentNumber: removeNonNumeric(data.document),
      documentType: DocumentTypeEnum.CPF,
      sessionAuthId: sessionStorage.getItem('sessionAuthId') || '',
      addressMetadata: {
        postalCode: removeNonNumeric(data.postalCode),
        street: data.street,
        number: data.number,
        complement: data.complement,
        district: data.district,
        city: data.city,
        state: data.state,
      },
      phones: [removeNonNumeric(data.phone)],
      persons: [
        {
          email: data.email,
          socialName: data.fantasyName || data.name,
          name: data.name,
          documentNumber: removeNonNumeric(data.document),
          genre: data.genre,
          birthDate: data.birthDate,
          phones: [removeNonNumeric(data.phone)],
          role: 'OWNER',
        },
      ],
    };

    createOnboarding(payload, {
      onSuccess: () => {
        navigate(links.onboarding.sended);
      },
    });
  };

  const handleGetAddress = async () => {
    const postalCode = hookForm.getValues('postalCode');
    if (postalCode.length === 9) {
      const response = await getAddressByPostalCode(postalCode);

      if (response.erro) {
        hookForm.setError('postalCode', {
          message: t('postalCodeError'),
        });
      } else {
        hookForm.clearErrors('postalCode');
      }

      if (response.logradouro === '' || response.bairro === '') {
        setPostalCodePartial(true);
      } else {
        setPostalCodePartial(false);
      }

      hookForm.setValue('street', response.logradouro);
      hookForm.setValue('district', response.bairro);
      hookForm.setValue('city', response.localidade);
      hookForm.setValue('state', response.uf);
    }
  };

  const handleClearPostalCode = () => {
    hookForm.setValue('postalCode', '');
    hookForm.setValue('street', '');
    hookForm.setValue('district', '');
    hookForm.setValue('city', '');
    hookForm.setValue('state', '');
    hookForm.setValue('number', '');
    hookForm.setValue('complement', '');
  };

  useEffect(() => {
    handleGetAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookForm.watch('postalCode')]);

  return (
    <Flex flexDir="column" gap={4}>
      <FormProvider {...hookForm}>
        <form onSubmit={hookForm.handleSubmit(handleSubmit)}>
          <Flex gap={4} h="100dvh" maxH="calc(100dvh - 242px)" overflowY="auto">
            <Flex flexDir="column" gap={6} w="full">
              <Text color="brand.800" fontSize="2xl" fontWeight={600}>
                {t('userData')}
              </Text>

              <Grid templateColumns="repeat(4, 1fr)" gap={4} w="full">
                <GridItem colSpan={2}>
                  <Input
                    label={t('document')}
                    name="document"
                    placeholder={t('documentPlaceholder')}
                    type="cpf"
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <Input
                    label={t('fantasyName')}
                    name="fantasyName"
                    placeholder={t('fantasyNamePlaceholder')}
                  />
                </GridItem>

                <GridItem colSpan={4}>
                  <Input
                    label={t('name')}
                    name="name"
                    placeholder={t('name')}
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <Input
                    label={t('birthDate')}
                    name="birthDate"
                    placeholder={t('birthDatePlaceholder')}
                    type="date"
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <Select
                    label={t('genre')}
                    name="genre"
                    placeholder={t('genrePlaceholder')}
                  >
                    {GENRES.map(genre => (
                      <option key={genre.value} value={genre.value}>
                        {genre.label}
                      </option>
                    ))}
                  </Select>
                </GridItem>
              </Grid>

              <Flex flexDir="column">
                <Text color="brand.800" fontSize="2xl" fontWeight={600}>
                  {t('yourAddress')}
                </Text>
              </Flex>

              <Grid templateColumns="repeat(6, 1fr)" gap={4} w="full">
                <GridItem
                  colSpan={{ base: 2, md: 2 }}
                  display="flex"
                  gap={2}
                  alignItems="end"
                >
                  <Input
                    label={t('postalCode')}
                    name="postalCode"
                    type="mask"
                    mask="99999-999"
                    placeholder={t('postalCodePlaceholder')}
                  />
                  <IconButton
                    aria-label="Buscar endereço"
                    icon={<Icon as={Trash} />}
                    onClick={handleClearPostalCode}
                  />
                </GridItem>

                <GridItem colSpan={{ base: 2, md: 2 }}>
                  <Input
                    label={t('number')}
                    name="number"
                    placeholder={t('numberPlaceholder')}
                  />
                </GridItem>

                <GridItem colSpan={{ base: 6, md: 2 }}>
                  <Input
                    label={t('complement')}
                    name="complement"
                    placeholder={t('complementPlaceholder')}
                  />
                </GridItem>

                {postalCodePartial && (
                  <>
                    <GridItem colSpan={{ base: 6, md: 3 }}>
                      <Input
                        label={t('street')}
                        name="street"
                        placeholder={t('streetPlaceholder')}
                      />
                    </GridItem>
                    <GridItem colSpan={{ base: 6, md: 3 }}>
                      <Input
                        label={t('district')}
                        name="district"
                        placeholder={t('districtPlaceholder')}
                      />
                    </GridItem>
                  </>
                )}
              </Grid>

              {/* caso não haja erro no campo postalCode, exibe o endereço */}
              {hookForm.getValues('city') && (
                <AddressCard
                  city={hookForm.watch('city')}
                  state={hookForm.watch('state')}
                  street={hookForm.watch('street')}
                  district={hookForm.watch('district')}
                  number={hookForm.watch('number')}
                  complement={hookForm.watch('complement')}
                />
              )}

              <Grid templateColumns="repeat(4, 1fr)" gap={4} w="full">
                <GridItem colSpan={4}>
                  <Text color="brand.800" fontSize="2xl" fontWeight={600}>
                    {t('yourContact')}
                  </Text>
                </GridItem>

                <GridItem colSpan={2}>
                  <Input
                    label={t('phone')}
                    name="phone"
                    placeholder={t('phonePlaceholder')}
                    type="phone"
                  />
                </GridItem>
                <GridItem colSpan={4}>
                  <Input
                    label={t('email')}
                    name="email"
                    placeholder={t('emailPlaceholder')}
                    type="email"
                  />
                </GridItem>

                <GridItem colSpan={4}>
                  <Alert status="info" justifyContent="start">
                    <AlertIcon />
                    {t('emailAlert')}
                  </Alert>
                </GridItem>
              </Grid>
            </Flex>
          </Flex>

          <Flex justifyContent="end">
            <Button
              type="submit"
              colorScheme="brand"
              w={240}
              isLoading={isLoading}
            >
              {t('sendData')}
            </Button>
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
};
